// @ts-nocheck
import { getSessionId } from '@aspendental/shared-utils/utils';

import { Maybe, IglobalThis } from '@/types';

import { objectKeyToSnake } from './camelCaseToSnake';

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace globalThis {
		let dataLayer: Array<Record<string, unknown>>;
		let dispatchDataLayer: (config: IDataLayerEventConfig) => void;
	}
}

globalThis.dispatchDataLayer = (config: IDataLayerEventConfig) => {
	dataLayerConfig({
		...config,
	});
};

export interface IDataLayerEventConfig {
	[key: string]: Maybe<string | boolean | number | undefined | Record<string, unknown>>;
}

export const dataLayerConfig = (eventConfig: IDataLayerEventConfig = { event: '' }) => {
	// transform object keys that come from contentful to snake case because InfoTrust
	// newConfig should be the same as eventConfig, just snake_case
	const newConfig = objectKeyToSnake(eventConfig);

	const { page_origin_url } = newConfig;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	globalThis.dataLayer = (globalThis as IglobalThis).dataLayer || [];

	const defaultConfig = {
		tag_session_id: getSessionId(),
		platform: 'NextJS',
		page_origin_name: globalThis?.document?.title,
		page_origin_url: page_origin_url ?? globalThis?.location?.href,
	};

	// Removing contentful_metadata and sys are temporary
	// those keys are only present due to how the manual events are needed to be used
	// will be deprecated during a Contentful clean up
	Object.keys(newConfig).forEach(
		(key) =>
			(key === '__typename' ||
				key === 'contentful_metadata' ||
				key === 'sys' ||
				newConfig[key] === undefined ||
				newConfig[key] === null ||
				newConfig[key] === '') &&
			delete newConfig[key]
	);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
	globalThis.dataLayer.push({
		...defaultConfig,
		...newConfig,
	});
};

export const mapSearchClickEvent = (params?: { numResults?: number }) => {
	const eventConfig = {
		event: 'mapbox_search_click',
		...(params && 'numResults' in params ? { num_results: params.numResults } : {}),
	};

	dataLayerConfig(eventConfig);
};

// Per the 2.0 DataLayer spec (TAP-1512). Kept as a constant so the event name is a
// single-line change if the spec settles on a different one.
export const SEARCH_RESULTS_EVENT = 'search_results';

export const SEARCH_TYPE_LOCATION = 'location';

/**
 * Pushes the `search_results` event once a search has resolved.
 *
 * Must be called *after* results come back. `searchResultCount` is the number of results
 * the user actually sees, so it matches the rendered list rather than the raw match count —
 * brands with `fetchAllLocationsOnEmptyResults` on backfill a zero-match search with every
 * facility and render them, and the event reports that.
 *
 * Zero-result searches are still reported: `dataLayerConfig` strips empty strings but not
 * `0`, so `search_result_count: 0` survives. A blank `searchTerm` is dropped entirely — that
 * key would be stripped and the GTM trigger would see a `search_results` event without it.
 */
export const searchResultsEvent = (params: { searchTerm: string; searchResultCount: number; searchType?: string }) => {
	// Trimmed here rather than at each call site: the URL-driven map search passes the raw
	// `?query` value while the search inputs pass a trimmed one, and untrimmed terms would
	// split into separate rows in reporting.
	const searchTerm = params.searchTerm.trim();

	if (!searchTerm) {
		return;
	}

	dataLayerConfig({
		event: SEARCH_RESULTS_EVENT,
		searchTerm,
		searchResultCount: params.searchResultCount,
		searchType: params.searchType ?? SEARCH_TYPE_LOCATION,
	});
};
